<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="companies"
      :locale="$i18n.locale"
      :no-data-text="$t('general.admin.tables.no_data_text')"
      :pagination.sync="pagination"
      :loading="$apollo.queries.companies.loading"
      :total-items="totalCount"
      class="admin-table"
    >
      <template v-slot:items="props">
        <td>{{ props.item.name }}</td>
        <td>{{ devicesCount(props.item.id) }}</td>
        <td>{{ usersCount(props.item.id) }}</td>
        <td class="justify-center align-center layout px-0 three-dots-container">
          <v-menu
            bottom
            center
            min-width="180px"
            content-class="custom-menu">
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                v-on="on">
                <v-icon>more_horiz</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-tile
                :key="1"
                @click="editCompany(props.item)">
                <v-list-tile-title class="text-xs-center link">{{ $t("general.admin.tables.edit") }}</v-list-tile-title>
              </v-list-tile>

              <v-list-tile
                :key="2"
                @click="openDeleteDialog(props.item)">
                <v-list-tile-title
                  class="text-xs-center link"
                >{{ $t("general.admin.tables.delete") }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </td>
      </template>
    </v-data-table>

    <v-edit-company-modal
      v-if="editDialog"
      :stored-company="editedCompany"
      @close="editDialog = false"
    />

    <v-edit-company-modal
      v-if="newDialog"
      new
      @close="newDialog = false"
    />

    <v-dialog
      v-model="deleteDialog"
      max-width="900px"
      content-class="custom-dialog">
      <v-card class="pa-5 custom-dialog-card">
        <v-card-title>
          <span
            class="headline"
          >{{ $t('general.dialogs.delete_header_pre') }} {{ deletedCompany.name }} {{ $t('general.dialogs.delete_header_post') }}</span>
        </v-card-title>

        <v-card-text>
          <v-container
            grid-list-md
            pa-0>
            <v-layout wrap>
              <v-flex
                v-show="formError"
                class="error-message"
                xs12>{{ formError }}</v-flex>
            </v-layout>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="blue darken-1"
            flat
            class="cancel"
            @click="close"
          >{{ $t('general.dialogs.cancel') }}</v-btn>
          <v-btn
            color="blue darken-1"
            flat
            class="confirm"
            @click="deleteCompany"
          >{{ $t('general.dialogs.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-speed-dial
      v-model="fab"
      bottom
      right
      fixed>
      <template v-slot:activator>
        <v-btn
          v-model="fab"
          color="#6480C9"
          dark
          fab
          class="mb-2"
          @click.stop="openNewDialog">
          <v-icon :style="{lineHeight: '58px'}">add</v-icon>
        </v-btn>
      </template>
    </v-speed-dial>
  </div>
</template>

<script>
// eslint-disable-next-line
import gql from 'graphql-tag';
import VEditCompanyModal from '@/components/EditCompanyModal.vue';

export default {
  name: 'CompaniesTable',

  components: {
    VEditCompanyModal,
  },

  data() {
    return {
      textInputRules: [value => !!value || this.$t('general.forms.required')],
      deleteDialog: false,
      deletedCompany: {},
      editDialog: false,
      editedCompany: {},
      fab: false,
      newDialog: false,
      formError: null,
      companies: [],
      pagination: {},
      totalCount: 0,
    };
  },

  created() {
    this.headers = [
      { text: 'Name', value: 'name' },
      { text: 'Geräte', value: 'devices' },
      { text: 'Benutzer', value: 'users' },
      { text: '', value: '', sortable: false },
    ];
  },

  apollo: {
    companies: {
      query: gql`
        query GetCompanies(
          $limit: Int,
          $offset: Int,
          $order_by: [group_order_by!],
        ) {
          group_aggregate {
            aggregate {
              count
            }
          }
          group(
            limit: $limit,
            offset: $offset,
            order_by: $order_by,
          ) {
            id
            name
            users {
              user {
                email
                firstname
                id
                lastname
                role
              }
            }
            devices {
              id
              key
              name
              project
            }
            projects {
              id
              name
            }
          }
        }
      `,
      variables() {
        const { sortBy, descending, page, rowsPerPage } = this.pagination;
        return {
          limit: rowsPerPage || 10,
          offset: rowsPerPage * (page - 1) || 0,
          order_by: sortBy ? { [sortBy]: descending ? 'desc' : 'asc' } : null,
        };
      },
      update(data) {
        this.totalCount = data.group_aggregate.aggregate.count;
        return data.group;
      },
    },
  },

  methods: {
    openNewDialog() {
      this.newDialog = true;
    },
    openDeleteDialog(item) {
      this.deletedCompany = item;
      this.deleteDialog = true;
    },
    editCompany(item) {
      this.editedCompany = Object.assign({}, item);
      this.editDialog = true;
    },
    deleteCompany() {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation($id: uuid!) {
              delete_group(where: { id: { _eq: $id } }) {
                affected_rows
              }
            }
          `,

          variables: {
            id: this.deletedCompany.id,
          },
        })
        .then(() => {
          this.close();
        })
        .catch((error) => {
          this.formError = error.message.includes('Foreign key violation') ?
            this.$t('general.admin.tabs.companies.delete_error') :
            this.$t('general.dialogs.error');
        });
    },
    close() {
      this.newDialog = false;
      this.editDialog = false;
      this.deleteDialog = false;
      this.formError = null;
      setTimeout(() => {
        this.editedCompany = Object.assign({}, this.defaultCompany);
        this.deletedCompany = Object.assign({}, this.defaultCompany);
      }, 300);
    },
    devicesCount(id) {
      if (this.companies.find(company => company.id === id)) {
        return this.companies.find(company => company.id === id).devices.length;
      }
      return 0;
    },
    usersCount(id) {
      if (this.companies.find(company => company.id === id)) {
        return this.companies.find(company => company.id === id).users.length;
      }
      return 0;
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
